import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import { Button, Empty, Spin, Table } from "antd";
import { fetchZendeskReport } from "util/zendeskAppApi";
import CauseDrawer from "./CauseDrawer";
import { ChangeChip, DailyBars } from "./parts";
import { topCauseShare, totalChangePct } from "./zendeskFormat";
import styles from "./style.module.scss";

/**
 * app.pivony.com/console/zendesk: the weekly root-cause report of the workspace's Zendesk connection.
 * "View tickets" opens one cause in a drawer; ?cause= (from "Open in Pivony" in the Zendesk sidebar)
 * opens it on load.
 */
const ZendeskPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  const params = new URLSearchParams(location.search);
  const subdomain = params.get("subdomain") || "";
  const causeParam = params.get("cause");

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    const res = await fetchZendeskReport(subdomain);
    if (!res || res.error || res.cancel) {
      setFailed(true);
      setData(null);
    } else {
      setData(res);
    }
    setLoading(false);
  }, [subdomain]);

  useEffect(() => {
    load();
  }, [load]);

  const openCause = (causeId) => {
    const next = new URLSearchParams(location.search);
    next.set("cause", String(causeId));
    history.push(`${location.pathname}?${next.toString()}`);
  };

  const closeCause = () => {
    const next = new URLSearchParams(location.search);
    next.delete("cause");
    next.delete("ticket");
    const q = next.toString();
    history.push(q ? `${location.pathname}?${q}` : location.pathname);
  };

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>
          <Spin />
        </div>
      </div>
    );
  }

  if (failed) {
    return (
      <div className={styles.page}>
        <h1 className={styles.title}>{t("zendeskPage.title")}</h1>
        <Empty description={t("zendeskPage.load_error")}>
          <Button onClick={load}>{t("zendeskPage.retry")}</Button>
        </Empty>
      </div>
    );
  }

  if (!data?.connected) {
    return (
      <div className={styles.page}>
        <h1 className={styles.title}>{t("zendeskPage.title")}</h1>
        <div className={styles.emptyCard}>
          <p>{t("zendeskPage.not_connected")}</p>
          <Button type="primary" onClick={() => history.push("/settings/integrations")}>
            {t("zendeskPage.connect")}
          </Button>
        </div>
      </div>
    );
  }

  const report = data.report || {};
  const causes = report.top_causes || [];
  const totalChange = totalChangePct(report.total_this_week, report.total_last_week);
  const share = topCauseShare(report);
  const maxWeek = Math.max(1, ...causes.map((c) => Math.max(c.this_week, c.last_week)));

  const columns = [
    {
      title: t("zendeskPage.col_cause"),
      key: "cause",
      render: (_, row) => (
        <>
          <div className={styles.causeLabel}>{row.label}</div>
          <div className={styles.tag}>{row.tag}</div>
        </>
      ),
    },
    {
      title: t("zendeskPage.col_weeks"),
      key: "weeks",
      render: (_, row) => (
        <>
          <div className={styles.track}>
            <div className={styles.fill} style={{ width: `${(row.this_week * 100) / maxWeek}%` }} />
          </div>
          <div className={styles.weekNums}>
            <b>{row.this_week}</b> <span className={styles.muted}>{t("zendeskPage.vs", { count: row.last_week })}</span>
          </div>
        </>
      ),
    },
    {
      title: t("zendeskPage.col_change"),
      key: "change",
      render: (_, row) => <ChangeChip changePct={row.change_pct} />,
    },
    {
      title: t("zendeskPage.col_negative"),
      key: "negative",
      render: (_, row) => (row.negative_pct === null || row.negative_pct === undefined ? "–" : `${row.negative_pct}%`),
    },
    {
      title: "",
      key: "open",
      align: "right",
      render: (_, row) => (
        <button type="button" className={styles.linkButton} onClick={() => openCause(row.cause_id)}>
          {t("zendeskPage.view_tickets")} →
        </button>
      ),
    },
  ];

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>{t("zendeskPage.title")}</h1>
          <div className={styles.muted}>{t("zendeskPage.subtitle")}</div>
        </div>
        <span className={`${styles.statusPill} ${data.status === "disconnected" ? styles.statusOff : ""}`}>
          ●{" "}
          {data.status === "disconnected" ? t("zendeskPage.disconnected") : t("zendeskPage.connected")} ·{" "}
          {data.subdomain}.zendesk.com
        </span>
      </div>

      <div className={styles.kpis}>
        <div className={styles.kpi}>
          <div className={styles.kpiValue}>
            {report.total_this_week} {totalChange !== null ? <ChangeChip changePct={totalChange} /> : null}
          </div>
          <div className={styles.kpiLabel}>{t("zendeskPage.kpi_this_week")}</div>
        </div>
        <div className={styles.kpi}>
          <div className={styles.kpiValue}>{report.analyzed_total}</div>
          <div className={styles.kpiLabel}>{t("zendeskPage.kpi_analyzed")}</div>
        </div>
        <div className={styles.kpi}>
          <div className={styles.kpiValue}>{(report.rising || []).length}</div>
          <div className={styles.kpiLabel}>{t("zendeskPage.kpi_rising")}</div>
        </div>
        <div className={styles.kpi}>
          <div className={styles.kpiValue}>{share === null ? "–" : `${share}%`}</div>
          <div className={styles.kpiLabel}>{t("zendeskPage.kpi_top_share")}</div>
        </div>
      </div>

      <div className={styles.grid}>
        <div className={styles.card}>
          <h2 className={styles.cardTitle}>{t("zendeskPage.daily_title")}</h2>
          <DailyBars values={report.daily_total_14d || []} />
          <div className={styles.axis}>
            <span>{t("zendeskPage.last_week")}</span>
            <span>{t("zendeskPage.this_week")}</span>
          </div>
        </div>
        <div className={styles.card}>
          <h2 className={styles.cardTitle}>{t("zendeskPage.rising_title")}</h2>
          {(report.rising || []).length ? (
            report.rising.map((r) => (
              <button type="button" key={r.id} className={styles.rise} onClick={() => openCause(r.cause_id)}>
                <span>{r.label}</span>
                <ChangeChip changePct={r.change_pct} />
              </button>
            ))
          ) : (
            <div className={styles.muted}>{t("zendeskPage.rising_none")}</div>
          )}
        </div>
      </div>

      <div className={`${styles.card} ${styles.tableCard}`}>
        <h2 className={styles.cardTitle}>{t("zendeskPage.table_title")}</h2>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={causes}
          pagination={false}
          size="middle"
          rowClassName={(row) => (String(row.cause_id) === causeParam ? styles.selectedRow : "")}
          onRow={(row) => ({ onClick: () => openCause(row.cause_id) })}
          locale={{ emptyText: t("zendeskPage.no_causes") }}
        />
      </div>

      <CauseDrawer causeId={causeParam} subdomain={subdomain || data.subdomain} onClose={closeCause} />
    </div>
  );
};

export default ZendeskPage;
